/**
 * CAOS-595 — the rich format on the Text kind: what a rich value may contain, and the one sanitiser
 * everything that reads or writes one goes through.
 *
 * THE ALLOW-LIST IS THE SUBSTANCE. A rich value is HTML, and the HTML in a record was written by
 * whoever could write the record — through the editor, but just as well through the API, an import
 * or a package. So nothing that shows a rich value trusts it:
 *   • the reader sanitises on every render;
 *   • the editor sanitises what it stores and what is pasted into it;
 *   • a toolbar button's inserted markup goes through the same function.
 * There is one sanitiser, so there is one answer to "what can a rich value contain". The developer
 * docs for the Text kind state these rules, and a test holds the docs and these lists equal.
 *
 * THE RULES.
 *   • Elements kept: {@link RICH_TEXT_ELEMENTS}.
 *   • Attributes kept: `href`, on `a` only, and only an absolute address using one of
 *     {@link RICH_TEXT_LINK_SCHEMES} with no user name or password in it. Every kept link is given
 *     `rel` {@link RICH_TEXT_LINK_REL}. A link whose address is refused keeps its text and loses the link.
 *   • Any other element is removed and its content is kept, sanitised by the same rules.
 *   • {@link RICH_TEXT_DROPPED_WITH_CONTENT} are removed together with their content, and so is anything
 *     outside the HTML namespace: their text is code, styling or form state, never prose.
 *   • Comments are removed.
 *
 * HOW. The value is parsed as the content of a `<template>` element. Template content is inert: nothing
 * in it runs, loads or fires an event, which is not true of every other way to parse a string (a
 * `DOMParser` document or a created document can still act on an `iframe`'s `srcdoc` in some
 * environments). The cleaned result is then BUILT node by node in the document it will be shown in. The
 * reader never serialises the cleaned tree and parses it a second time on the way to the screen, so a
 * string that would parse differently the second time has no second parse to exploit.
 */

/** The elements a rich value keeps. */
export const RICH_TEXT_ELEMENTS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'h2', 'h3', 'a',
] as const;

/** The attributes a kept element keeps, by element. An element not named here keeps none. */
export const RICH_TEXT_ATTRIBUTES: Readonly<Record<string, readonly string[]>> = { a: ['href'] };

/** The address schemes a kept link may use. */
export const RICH_TEXT_LINK_SCHEMES = ['http:', 'https:', 'mailto:'] as const;

/** The `rel` every kept link carries. */
export const RICH_TEXT_LINK_REL = 'noopener noreferrer nofollow';

/** The elements removed together with everything inside them. */
export const RICH_TEXT_DROPPED_WITH_CONTENT = [
  'script', 'style', 'template', 'iframe', 'object', 'embed', 'svg', 'math', 'noscript', 'textarea', 'select',
] as const;

const ELEMENTS: ReadonlySet<string> = new Set(RICH_TEXT_ELEMENTS);
const DROPPED: ReadonlySet<string> = new Set(RICH_TEXT_DROPPED_WITH_CONTENT);
const HTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
/**
 * Past this many levels of nesting the rest is kept as plain text. No document a person writes is
 * this deep, and a walk with no floor is a stack a hostile value can exhaust.
 */
const MAX_LEVEL = 256;

/** The address a kept link may point at, normalised, or `null` when the link must lose its address. */
export function safeLinkHref(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (!(RICH_TEXT_LINK_SCHEMES as readonly string[]).includes(url.protocol)) return null;
  // An address with a user name in it reads as one host and resolves to another.
  if (url.username !== '' || url.password !== '') return null;
  return url.href;
}

function copyAllowed(from: Node, into: Node, doc: Document, level: number): void {
  if (level > MAX_LEVEL) {
    into.appendChild(doc.createTextNode(from.textContent ?? ''));
    return;
  }
  for (const node of Array.from(from.childNodes)) {
    if (node.nodeType === TEXT_NODE) {
      into.appendChild(doc.createTextNode(node.nodeValue ?? ''));
      continue;
    }
    if (node.nodeType !== ELEMENT_NODE) continue; // comments and processing instructions
    const el = node as Element;
    if (el.namespaceURI !== HTML_NAMESPACE) continue;
    const tag = el.localName.toLowerCase();
    if (DROPPED.has(tag)) continue;
    if (!ELEMENTS.has(tag)) {
      copyAllowed(el, into, doc, level + 1);
      continue;
    }
    if (tag === 'a') {
      const href = safeLinkHref(el.getAttribute('href'));
      if (href === null) {
        copyAllowed(el, into, doc, level + 1);
        continue;
      }
      const link = doc.createElement('a');
      link.setAttribute('href', href);
      link.setAttribute('rel', RICH_TEXT_LINK_REL);
      copyAllowed(el, link, doc, level + 1);
      into.appendChild(link);
      continue;
    }
    const kept = doc.createElement(tag);
    copyAllowed(el, kept, doc, level + 1);
    into.appendChild(kept);
  }
}

/** The sanitised content of a rich value, built in `doc`: the document it will be shown in. */
export function richTextFragment(html: unknown, doc: Document = document): DocumentFragment {
  const fragment = doc.createDocumentFragment();
  if (typeof html !== 'string' || html === '') return fragment;
  const inert = doc.createElement('template');
  inert.innerHTML = html;
  copyAllowed(inert.content, fragment, doc, 0);
  return fragment;
}

/** A rich value reduced to what the allow-list keeps, as HTML. This is what the editor stores. */
export function sanitizeRichText(html: unknown): string {
  const doc = document.implementation.createHTMLDocument('');
  const holder = doc.createElement('div');
  holder.append(richTextFragment(html, doc));
  return holder.innerHTML;
}

const BLOCKS: ReadonlySet<string> = new Set(['p', 'li', 'blockquote', 'pre', 'h2', 'h3', 'ul', 'ol']);

/** A rich value's words, with a line break between blocks: for a place that shows one line of text. */
export function richTextToPlainText(html: unknown): string {
  const fragment = richTextFragment(html, document.implementation.createHTMLDocument(''));
  const parts: string[] = [];
  const walk = (node: Node): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === TEXT_NODE) {
        parts.push(child.nodeValue ?? '');
      } else if (child.nodeType === ELEMENT_NODE) {
        const tag = (child as Element).localName;
        if (tag === 'br') {
          parts.push('\n');
          continue;
        }
        walk(child);
        if (BLOCKS.has(tag)) parts.push('\n');
      }
    }
  };
  walk(fragment);
  return parts
    .join('')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/ *\n[\n ]*/g, '\n')
    .trim();
}

/** Whether a rich value has no words in it once sanitised (`<p><br></p>` is empty). */
export function isRichTextEmpty(html: unknown): boolean {
  return richTextToPlainText(html) === '';
}

/** A web address, or an email address, written in plain text. */
const LINKABLE = /\bhttps?:\/\/[^\s<>"]*[^\s<>".,;:!?'")\]}]|\b[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}\b/g;

/**
 * Plain-text web and email addresses under `root` made into links, the way a reader shows them.
 * Text already inside a link, `code` or `pre` is left alone, and an address the link rules refuse
 * stays text.
 */
export function linkifyRichText(root: Node, doc: Document = document): void {
  const texts: Text[] = [];
  const collect = (node: Node, skip: boolean): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === TEXT_NODE) {
        if (!skip) texts.push(child as Text);
      } else if (child.nodeType === ELEMENT_NODE) {
        const tag = (child as Element).localName;
        collect(child, skip || tag === 'a' || tag === 'code' || tag === 'pre');
      }
    }
  };
  collect(root, false);
  for (const text of texts) {
    const value = text.nodeValue ?? '';
    const pieces: Node[] = [];
    let last = 0;
    for (const match of value.matchAll(LINKABLE)) {
      const found = match[0];
      const href = safeLinkHref(/^https?:/i.test(found) ? found : `mailto:${found}`);
      if (href === null) continue;
      if (match.index! > last) pieces.push(doc.createTextNode(value.slice(last, match.index)));
      const link = doc.createElement('a');
      link.setAttribute('href', href);
      link.setAttribute('rel', RICH_TEXT_LINK_REL);
      link.textContent = found;
      pieces.push(link);
      last = match.index! + found.length;
    }
    if (pieces.length === 0) continue;
    if (last < value.length) pieces.push(doc.createTextNode(value.slice(last)));
    const parent = text.parentNode!;
    for (const piece of pieces) parent.insertBefore(piece, text);
    parent.removeChild(text);
  }
}
